import { useCallback, useEffect, useState } from "react";

import {
  faArrowLeft,
  faCheckCircle,
  faChevronDown,
  faChevronUp,
  faCopy,
  faEye,
  faShareNodes,
  faUserCircle,
  faWavePulse,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import { UseQueryResult } from "@tanstack/react-query";
import { differenceInDays, format, parseISO } from "date-fns";
import { get, isEqual, isObject } from "lodash-es";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { twJoin } from "tailwind-merge";
import { useDebouncedCallback } from "use-debounce";

import { CasePatchBody } from "src/api/endpoints";
import { UseUploadFileReturn, useUploadFile } from "src/api/filesApi/queries";
import {
  useReviewCase,
  useReviewCaseMutation,
  useReviewCaseSubmit,
  useReviewCases,
} from "src/api/queries";
import { useWorkspaceUsers } from "src/api/taktile/queries";
import {
  ReviewCaseDataAvailability,
  ReviewCaseResponseData,
  ReviewCaseStatusPending,
  ReviewCaseWithDecisionData,
} from "src/api/types";
import { Button } from "src/base-components/Button";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { Pill } from "src/base-components/Pill";
import { CustomPopover } from "src/base-components/Popover";
import {
  GetFileEnvFilesFileIdGetEnvEnum,
  UploadFileEnvFilesPostEnvEnum,
} from "src/clients/files/api";
import { isValidGroupingId } from "src/decisionsOverview/utils";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { TAKTILE_TEAM_NOTIFIED } from "src/design-system/Toast/constants";
import { toastActions } from "src/design-system/Toast/utils";
import { ExcludesFalse } from "src/flow/types";
import { useCapabilities } from "src/hooks/useCapabilities";
import { DetailHeader } from "src/layout/DetailHeader";
import { Card } from "src/manualReview/reviewCaseCommon/Card";
import {
  Form as FormBase,
  MR_FORM_ELEMENTS_PREFIX,
} from "src/manualReview/reviewCaseCommon/Form";
import { FormSide } from "src/manualReview/reviewCaseCommon/FormSide";
import { SkeletonLine } from "src/manualReview/reviewCaseCommon/SkeletonLine";
import { SubHeader as SubHeaderBase } from "src/manualReview/reviewCaseCommon/SubHeader";
import { StatusPicker } from "src/manualReview/StatusPicker";
import { UserPicker } from "src/manualReview/UserPicker";
import {
  DEFAULT_FILTERS,
  useReviewQueueFilters,
} from "src/manualReview/useReviewFilters";
import {
  convertFiltersToParams,
  isDefaultFiltersState,
} from "src/manualReview/utils";
import { queryClient } from "src/queryClient";
import {
  useFlowContext,
  useWorkspaceContext,
} from "src/router/routerContextHooks";
import {
  DashboardPageParams,
  getBaseUrl,
  getUrlFlowDecisionHistory,
  getUrlToDecisionsOverview,
  getUrlToHistoricDecisionFromCase,
  getUrlToReviewQueue,
  ManualReviewPageParams,
} from "src/router/urls";
import { copyTextToClipboard } from "src/utils/clipboard";
import { dateFromNow } from "src/utils/datetime";
import { logger } from "src/utils/logger";
import {
  isBadRequest,
  isConflictError,
  isPreconditionError,
} from "src/utils/predicates";
import { useParamsDecode } from "src/utils/useParamsDecode";

const useSiblingCases = (
  reviewCase: ReviewCaseWithDecisionData | undefined,
  flowSlug: string,
) => {
  const { workspace } = useWorkspaceContext();
  const [_filters] = useReviewQueueFilters();
  const [filters, setFilters] = useState(_filters);
  const cases = useReviewCases(
    workspace.base_url,
    flowSlug,
    {
      ...convertFiltersToParams(filters),
      environment: reviewCase?.decision.is_sandbox ? "sandbox" : "prod",
      limit: 200,
    },
    { enabled: Boolean(reviewCase) },
  );

  const allCases = cases.data?.pages.flatMap((page) => page.cases) ?? [];
  const caseIndex = allCases.findIndex((rCase) => rCase.id === reviewCase?.id);

  useEffect(() => {
    if (caseIndex === -1 && !cases.isFetching && cases.hasNextPage) {
      cases.fetchNextPage();
    }
    // Fallback to default filters if for some reason we couldn't find the case
    if (
      caseIndex === -1 &&
      !cases.isFetching &&
      !cases.hasNextPage &&
      !isDefaultFiltersState(filters)
    ) {
      setFilters(DEFAULT_FILTERS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    caseIndex,
    cases.isFetching,
    cases.hasNextPage,
    cases.fetchNextPage,
    filters,
  ]);

  return {
    prevCaseId: caseIndex > 0 ? allCases[caseIndex - 1].id : null,
    nextCaseId:
      caseIndex < allCases.length - 1 && caseIndex !== -1
        ? allCases[caseIndex + 1].id
        : null,
  } as const;
};

const SiblingButtons: React.FC<{
  to: string;
  icon: IconDefinition;
  disabled: boolean;
  fileUpload: UseUploadFileReturn;
}> = ({ to, icon, disabled, fileUpload }) => (
  <Link
    className={twJoin(
      "rounded-md border border-gray-200 p-[2px]",
      disabled && "pointer-events-none opacity-50",
    )}
    relative="path"
    to={to}
    onClick={() => {
      fileUpload.removeAllFiles();
    }}
  >
    <Icon icon={icon} size="2xs" />
  </Link>
);

const DATA_AVAILABILITY_TOAST_ID = "data-availability-pending";

const useDataAvailabilityToast = (
  dataAvailability: ReviewCaseDataAvailability | undefined,
) => {
  useEffect(() => {
    if (dataAvailability === "pending") {
      toastActions.loading({
        id: DATA_AVAILABILITY_TOAST_ID,
        title: "Decision data loading",
        description:
          "Decision data is still being processed. It will appear automatically once ready.",
        duration: null,
        isDismissible: false,
      });
    } else if (dataAvailability === "available") {
      toastActions.dismiss(DATA_AVAILABILITY_TOAST_ID);
    }

    return () => {
      toastActions.dismiss(DATA_AVAILABILITY_TOAST_ID);
    };
  }, [dataAvailability]);
};

export const ReviewCaseContent: React.FC = () => {
  const { workspace, flow } = useFlowContext();
  const { case_id: caseId, orgId } = useParamsDecode<ManualReviewPageParams>();
  const fileUpload = useUploadFile();

  const reviewCaseQuery = useReviewCase(workspace.base_url, flow.slug, caseId, {
    refetchInterval: (query) => {
      if (query.state.error) return false;
      if (query.state.data?.data_availability === "available") return false;
      return 1000 * 60;
    },
  });
  const { nextCaseId } = useSiblingCases(reviewCaseQuery.data, flow.slug);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useDataAvailabilityToast(reviewCaseQuery.data?.data_availability);

  const onSubmit = (updatedCase?: ReviewCaseWithDecisionData) => {
    searchParams.set(
      "environment",
      updatedCase?.decision.is_sandbox ? "sandbox" : "prod",
    );
    if (nextCaseId) {
      navigate(`../${nextCaseId}?${searchParams.toString()}`, {
        relative: "path",
      });
    } else {
      // Navigate back to the queue
      navigate(`../?${searchParams.toString()}`, { relative: "path" });
    }
  };

  return (
    <FloatingWindowsProvider>
      <div className="flex h-full flex-1 flex-col divide-y divide-gray-200">
        {(() => {
          if (reviewCaseQuery.error) {
            return (
              <NotFoundCaseContent
                flowId={flow.id}
                orgId={orgId}
                workspaceId={workspace.id}
              />
            );
          }

          if (reviewCaseQuery.data?.status === "completed") {
            return (
              <CompletedCaseContent
                flowId={flow.id}
                orgId={orgId}
                reviewCaseQuery={reviewCaseQuery}
                workspaceId={workspace.id}
              />
            );
          }
          const env = reviewCaseQuery.data?.decision.is_sandbox
            ? "sandbox"
            : "live";

          return (
            <>
              <Header
                fileUpload={fileUpload}
                flowSlug={flow.slug}
                orgId={orgId}
                reviewCaseQuery={reviewCaseQuery}
                wsId={workspace.id}
              />
              <div className="flex min-h-0 flex-1">
                <div className="flex flex-1 flex-col bg-gray-100">
                  <SubHeader reviewCaseQuery={reviewCaseQuery} />
                  <Card
                    env={env as GetFileEnvFilesFileIdGetEnvEnum}
                    externalData={reviewCaseQuery.data?.external_data}
                    fileUpload={fileUpload}
                    highlights={reviewCaseQuery.data?.highlights ?? []}
                    inspectData={reviewCaseQuery.data?.inspect_data}
                    isLoading={!reviewCaseQuery.data}
                  />
                </div>
                <FormSide
                  description={
                    reviewCaseQuery.data?.response.schema.description
                  }
                  isLoading={!reviewCaseQuery.data}
                  nodeName={reviewCaseQuery.data?.node_name}
                >
                  {reviewCaseQuery.data && (
                    <ReviewCaseActionForm
                      key={reviewCaseQuery.data.id}
                      fileUpload={fileUpload}
                      flowSlug={flow.slug}
                      reviewCase={reviewCaseQuery.data}
                      onSubmit={onSubmit}
                    />
                  )}
                </FormSide>
              </div>
            </>
          );
        })()}
      </div>
    </FloatingWindowsProvider>
  );
};

const openEntityDecisionsView = ({
  orgId,
  workspaceId,
  entityId,
}: {
  orgId: string;
  workspaceId: string;
  entityId: string;
}) => {
  window.open(
    getBaseUrl() +
      getUrlToDecisionsOverview(orgId, workspaceId, {
        entityId,
      }),
    "_blank",
  );
};

const Header: React.FC<{
  fileUpload: UseUploadFileReturn;
  reviewCaseQuery: UseQueryResult<ReviewCaseWithDecisionData, Error>;
  orgId: string;
  wsId: string;
  flowSlug: string;
}> = ({ reviewCaseQuery, orgId, wsId, flowSlug, fileUpload }) => {
  const { search } = useLocation();
  const { prevCaseId, nextCaseId } = useSiblingCases(
    reviewCaseQuery.data,
    flowSlug,
  );
  const { decisionHistory } = useCapabilities();

  const groupingIdValue =
    reviewCaseQuery.data?.decision.grouping_id ?? undefined;

  return (
    <DetailHeader
      actions={
        reviewCaseQuery.data && (
          <div className="flex space-x-2">
            <CustomPopover
              button={
                <Button iconLeft={faShareNodes} size="sm" variant="secondary">
                  {" "}
                  Share
                </Button>
              }
            >
              <CustomPopover.Button className="w-60 cursor-pointer py-2">
                {[
                  { name: "link", value: window.location.href },
                  !!groupingIdValue && {
                    name: "Grouping ID",
                    value: groupingIdValue,
                  },
                  {
                    name: "Decision ID",
                    value: reviewCaseQuery.data.decision.id,
                  },
                ]
                  .filter(Boolean as unknown as ExcludesFalse)
                  .map((element) => (
                    <div
                      key={element.name}
                      className="flex h-12 items-center justify-between px-4 py-2.5 font-inter-normal-13px text-gray-800 hover:bg-gray-50"
                      onClick={() => {
                        copyTextToClipboard(element.value);
                        toastActions.success({ title: "Copied to clipboard!" });
                      }}
                    >
                      Copy {element.name}
                      <Icon icon={faCopy} size="xs" />
                    </div>
                  ))}
              </CustomPopover.Button>
            </CustomPopover>
            {decisionHistory.canAccess && (
              <Link
                rel="noopener noreferrer"
                target="_blank"
                to={getUrlToHistoricDecisionFromCase(
                  orgId,
                  wsId,
                  reviewCaseQuery.data,
                )}
              >
                <Button iconLeft={faEye} size="sm" variant="secondary">
                  Trace decision
                </Button>
              </Link>
            )}
            {groupingIdValue && isValidGroupingId(groupingIdValue) && (
              <EllipsisOptionsDropdown
                elements={[
                  {
                    key: "nav",
                    nav: "View related decisions",
                    subtitle: (
                      <>
                        Show all decisions with the same Grouping ID{" "}
                        <Pill size="sm" variant="gray" maxWidth>
                          <Pill.Text fontType="code">
                            {groupingIdValue}
                          </Pill.Text>
                        </Pill>
                      </>
                    ),
                    action: () => {
                      if (groupingIdValue) {
                        openEntityDecisionsView({
                          orgId,
                          workspaceId: wsId,
                          entityId: groupingIdValue,
                        });
                      }
                    },
                  },
                ]}
              />
            )}
          </div>
        )
      }
      main={
        reviewCaseQuery.data ? (
          <>
            <Link
              relative="path"
              to={`..${search}`}
              onClick={() => {
                fileUpload.removeAllFiles();
              }}
            >
              <Icon icon={faArrowLeft} size="xs" />
            </Link>
            <p className="ml-4 font-inter-semibold-13px">
              #{reviewCaseQuery.data.number}
            </p>
            <div className="ml-4 flex gap-x-2">
              <SiblingButtons
                disabled={!nextCaseId}
                fileUpload={fileUpload}
                icon={faChevronDown}
                to={`../${nextCaseId}${search}`}
              />
              <SiblingButtons
                disabled={!prevCaseId}
                fileUpload={fileUpload}
                icon={faChevronUp}
                to={`../${prevCaseId}${search}`}
              />
            </div>
          </>
        ) : (
          <SkeletonLine className="ml-4.5 h-3 w-56" />
        )
      }
    />
  );
};

const SubHeader: React.FC<{
  reviewCaseQuery: UseQueryResult<ReviewCaseWithDecisionData, Error>;
}> = ({ reviewCaseQuery }) => {
  const { workspace, flow, orgId } = useFlowContext();
  const { case_id: caseId } = useParamsDecode<ManualReviewPageParams>();

  const users = useWorkspaceUsers(orgId, workspace.id, {
    include_deactivated: false,
  });

  const caseMutation = useReviewCaseMutation(
    workspace.base_url,
    flow.slug,
    caseId,
  );

  const updateCase = async (data: CasePatchBody) => {
    try {
      await caseMutation.mutateAsync({
        ...data,
        etag: reviewCaseQuery.data?.etag,
      });
    } catch (e) {
      const fieldName = data.assignee ? "assignee" : "status";
      if (isPreconditionError(e)) {
        await queryClient.invalidateQueries({
          queryKey: ["reviewCase", workspace.base_url, flow.slug, caseId],
        });
        toastActions.failure({
          title: `Failed to update ${fieldName}`,
          description: "Please try again",
        });
      } else {
        toastActions.failure({
          title: `Failed to update ${fieldName}`,
          description: TAKTILE_TEAM_NOTIFIED,
        });
      }
      logger.error(e);
    }
  };

  const updateAssignee = async (userId: Nullable<string>) =>
    updateCase({ assignee: userId });
  const updateStatus = async (status: ReviewCaseStatusPending) =>
    updateCase({ status });

  if (!(reviewCaseQuery.data && users.isFetched)) {
    return <SubHeaderBase isLoading />;
  }

  return (
    <SubHeaderBase
      assignedTo={
        <UserPicker
          users={users.data ?? []}
          value={reviewCaseQuery.data.assignee}
          workspaceId={workspace.id}
          onChange={updateAssignee}
        />
      }
      requestTime={
        <p className="truncate">
          {format(
            parseISO(reviewCaseQuery.data.decision.start_time),
            "dd/MM/yyyy HH:mm:ss",
          )}
        </p>
      }
      status={
        <StatusPicker
          value={reviewCaseQuery.data.status}
          onChange={updateStatus}
        />
      }
      timeSinceRequest={<p>{dateFromNow(reviewCaseQuery.data.created_at)}</p>}
    />
  );
};

/*
This form has two main events: save and submit.
Save is done automatically when the form changes
and submit needs to be explicitly triggered and confirmed.

Some edge cases arise:

1. More saving is needed when other save action is still pending
  Solution: We maintain a "needsSave" flag and a useEffect will trigger
  save if the flag is set and the mutation is not loading

2. Save is triggered after the form has been submitted (as saves are debounced and submit is not)
  Solution: On submit the pending invocations to the debounced callback are cancelled

3. Submit is triggered while a save is still ongoing
  Solution: We maintain a "needsSubmit" flag which is similar to the #1
  but it also has a callback that needs to be called on submit's end
  for the confirmation modal to close
*/
export const ReviewCaseActionForm: React.FC<{
  reviewCase: ReviewCaseWithDecisionData;
  flowSlug: string;
  fileUpload: UseUploadFileReturn;
  onSubmit: (updatedCase?: ReviewCaseWithDecisionData) => void;
}> = ({ reviewCase, flowSlug, onSubmit, fileUpload }) => {
  const MAX_REVIEW_CASE_AGE_DAYS = 60;
  const isCaseExpired =
    differenceInDays(new Date(), parseISO(reviewCase.created_at)) >
    MAX_REVIEW_CASE_AGE_DAYS;

  const { decisionHistory } = useCapabilities();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const { workspace } = useWorkspaceContext();
  const { orgId } = useParamsDecode<DashboardPageParams>();

  const caseMutation = useReviewCaseMutation(
    workspace.base_url,
    flowSlug,
    reviewCase.id,
  );
  const caseSubmit = useReviewCaseSubmit(
    workspace.base_url,
    flowSlug,
    reviewCase.id,
  );

  const form = useForm<ReviewCaseResponseData>({
    defaultValues: reviewCase.response.data ?? undefined,
  });

  useEffect(() => {
    const erroredFields = Object.keys(form.formState.errors);
    if (erroredFields.length > 0 && !isConfirmationModalOpen) {
      const erroredElements = erroredFields
        .map((field) =>
          document.getElementById(`${MR_FORM_ELEMENTS_PREFIX}_${field}`),
        )
        .filter((element) => element !== null) as HTMLElement[];

      erroredElements[0]?.scrollIntoView({ behavior: "smooth" });
    }
  }, [form.formState.errors, isConfirmationModalOpen]);

  const saveMutateAsync = caseMutation.mutateAsync;
  const saveForm = useCallback(
    (data: ReviewCaseResponseData = form.getValues()) => {
      const responseData = structuredClone(data);

      if (isEqual(responseData, reviewCase.response.data)) {
        return Promise.resolve(reviewCase);
      }

      return saveMutateAsync({
        response_data: responseData,
        etag: reviewCase.etag,
      });
    },
    [form, reviewCase, saveMutateAsync],
  );

  const [needsSave, setNeedsSave] = useState(false);

  const handleOnChange = useDebouncedCallback(() => {
    if (caseMutation.isPending) {
      setNeedsSave(true);
    } else {
      saveForm();
    }
  }, 1000);

  useEffect(() => {
    // Check if a save event was triggered while
    // the last save was in progress
    if (needsSave && !caseMutation.isPending) {
      setNeedsSave(false);
      saveForm();
    }
  }, [caseMutation.isPending, needsSave, saveForm]);

  const submitFiles = async (
    fileUpload: UseUploadFileReturn,
    data: ReviewCaseResponseData,
  ): Promise<ReviewCaseResponseData> => {
    const result: ReviewCaseResponseData = { ...data };
    for (const [key, schemaProperty] of Object.entries(
      reviewCase.response.schema.properties,
    )) {
      const value = get(data, key);

      const isFileKey =
        "type" in schemaProperty &&
        schemaProperty.type === "string" &&
        schemaProperty.$comment === "file_upload";

      const isMultiFilesKey =
        "type" in schemaProperty &&
        schemaProperty.type === "array" &&
        schemaProperty.$comment === "files_upload";

      if (value === undefined && !isFileKey && !isMultiFilesKey) {
        continue;
      }

      if (isFileKey) {
        const selectedFile = fileUpload.getFile(key);

        if (selectedFile) {
          const env = reviewCase.decision.is_sandbox ? "sandbox" : "live";
          const fileId = await fileUpload.onFileUpload(
            selectedFile,
            key,
            env as UploadFileEnvFilesPostEnvEnum,
            "manual-review",
          );
          if (fileId) {
            result[key] = fileId;
          }
          fileUpload.onFileRemove(key);
        }
      }

      // Multi-file ("files" type): upload each file individually
      // and collect the resulting file IDs into an array.
      if (isMultiFilesKey) {
        const selectedFiles = fileUpload.getFilesForField(key);
        const env = reviewCase.decision.is_sandbox ? "sandbox" : "live";
        const fileIds: string[] = [];

        for (const [fileKey, file] of selectedFiles) {
          const fileId = await fileUpload.onFileUpload(
            file,
            fileKey,
            env as UploadFileEnvFilesPostEnvEnum,
            "manual-review",
          );
          if (fileId) {
            fileIds.push(fileId);
          }
          fileUpload.onFileRemove(fileKey);
        }

        if (fileIds.length > 0) {
          result[key] = fileIds;
        }
      }
    }

    return result;
  };

  const refineResponseData = async (
    data: ReviewCaseResponseData,
  ): Promise<ReviewCaseResponseData> => {
    const result: ReviewCaseResponseData = {};

    for (const [key, schemaProperty] of Object.entries(
      reviewCase.response.schema.properties,
    )) {
      const isFileKey =
        "type" in schemaProperty &&
        schemaProperty.type === "string" &&
        schemaProperty.$comment === "file_upload";

      const isMultiFilesKey =
        "type" in schemaProperty &&
        schemaProperty.type === "array" &&
        schemaProperty.$comment === "files_upload";

      if (isFileKey || isMultiFilesKey) {
        continue;
      }

      const value = get(data, key);

      // Empty strings should not make it to the final response
      // these empty strings come from empty input fields
      // and if we got to this point is because they are not required
      if (
        "type" in schemaProperty &&
        ["number", "integer", "string"].includes(schemaProperty.type) &&
        typeof value === "string" &&
        value.trim() === ""
      ) {
        continue;
      }
      // We don't validate on save so we have to delay the parsing of
      // input field values as numbers until we submit
      else if (
        "type" in schemaProperty &&
        ["number", "integer"].includes(schemaProperty.type)
      ) {
        result[key] = parseFloat(value as string);
      } else {
        result[key] = value;
      }
    }

    return result;
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [needsSubmit, setNeedsSubmit] = useState<
    { status: false } | { status: true; onSubmitEnd: () => void }
  >({ status: false });

  const submitHandler: SubmitHandler<ReviewCaseResponseData> = async (data) => {
    // Make sure we don't submit twice
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setNeedsSave(false);

    // We cancel pending saves as we'll
    // do a refined data save before submitting
    handleOnChange.cancel();

    if (caseMutation.isPending) {
      return new Promise<undefined>((resolve) => {
        setIsSubmitting(false);
        setNeedsSubmit({ status: true, onSubmitEnd: () => resolve(undefined) });
      });
    }
    try {
      const refinedData = await refineResponseData(data);
      const refinedDataWithFiles = await submitFiles(fileUpload, refinedData);
      const updatedCase = await saveForm(refinedDataWithFiles);
      await caseSubmit.mutateAsync({
        etag: updatedCase.etag,
      });
      onSubmit(updatedCase);
    } catch (e) {
      if (isPreconditionError(e)) {
        await queryClient.invalidateQueries({
          queryKey: ["reviewCase", workspace.base_url, flowSlug, reviewCase.id],
        });
        toastActions.failure({
          title: `Failed to submit the case`,
          description: "Please try again",
        });
      } else if (isConflictError(e)) {
        toastActions.failure({
          title: `Failed to submit the case`,
          description: "This case has already been submitted",
        });
      } else if (isBadRequest(e)) {
        // This is fallback, it should not possible to reach this point
        // Because we should have the same validations on the frontend side
        toastActions.failure({
          title: "Review case validation failed",
          description:
            isObject(e.response?.data) && "detail" in e.response.data
              ? (e.response.data.detail as string)
              : "Unknown error",
        });
        logger.error("Unexpected review case validation failure", e);
      } else {
        toastActions.failure({
          title: `Failed to submit the case`,
          description: TAKTILE_TEAM_NOTIFIED,
        });
      }
      logger.error(e);
    } finally {
      setIsSubmitting(false);
      if (needsSubmit.status) {
        needsSubmit.onSubmitEnd();
      }
    }
  };

  const handleSubmit = form.handleSubmit(submitHandler);

  useEffect(() => {
    // Check if a submit event is waiting
    // for a save to finish
    if (needsSubmit.status && !caseMutation.isPending) {
      setNeedsSubmit({ status: false });
      handleSubmit();
    }
  }, [caseMutation.isPending, handleSubmit, needsSubmit.status]);

  const getError = (error: unknown) => {
    if (!error) return;

    if (isPreconditionError(error)) {
      return "Autosaving failed. Refresh to see the most recent state";
    }

    return "We couldn't save the last state of the form";
  };

  // Keep the relative, otherwise it creates overflow outside of the html element
  // even if the overflow is contained by the parent element
  return (
    <>
      <FormBase
        disabled={isSubmitting || isCaseExpired}
        disabledReason={
          isCaseExpired
            ? `This review case is older than ${MAX_REVIEW_CASE_AGE_DAYS} days and can no longer be submitted.`
            : undefined
        }
        error={getError(caseMutation.error)}
        fileUpload={fileUpload}
        form={form}
        schema={reviewCase.response.schema}
        onChange={handleOnChange}
        onClickSubmit={() => setIsConfirmationModalOpen(true)}
      />
      <ConfirmationModal
        cancelationButtonText="Cancel"
        confirmationButtonText="Submit"
        open={isConfirmationModalOpen}
        title="Submit manual review input?"
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirm={async () => {
          try {
            await handleSubmit();
          } finally {
            setIsConfirmationModalOpen(false);
          }
        }}
      >
        <p className="font-inter-normal-13px text-gray-500">
          The input cannot be changed once the review is submitted.
          {decisionHistory.canAccess && (
            <>
              {" "}
              To further track this decision,{" "}
              <Link
                className="text-indigo-500"
                rel="noopener noreferrer"
                target="_blank"
                to={getUrlToHistoricDecisionFromCase(
                  orgId,
                  workspace.id,
                  reviewCase,
                )}
              >
                find it in the decision history
              </Link>
              .
            </>
          )}
        </p>
      </ConfirmationModal>
    </>
  );
};

const CompletedCaseContent: React.FC<{
  orgId: string;
  workspaceId: string;
  flowId: string;
  reviewCaseQuery: UseQueryResult<ReviewCaseWithDecisionData, Error>;
}> = ({ orgId, workspaceId, flowId, reviewCaseQuery }) => {
  const { decisionHistory } = useCapabilities();
  const { search } = useLocation();

  return (
    <div className="h-screen">
      <EmptyState
        actionButton={
          decisionHistory.canAccess
            ? {
                label: "Go to the decision history",
                icon: faWavePulse,
                linkTo: getUrlFlowDecisionHistory(orgId, workspaceId, flowId, {
                  decisionId: reviewCaseQuery.data?.decision.id,
                }),
              }
            : {
                label: "Go to the review queue",
                icon: faUserCircle,
                linkTo: getUrlToReviewQueue(orgId, workspaceId, flowId, search),
              }
        }
        dataLoc="completed-case"
        description={
          decisionHistory.canAccess
            ? "To further track the related decision find it in the decision history"
            : undefined
        }
        headline="This Manual review item was already submitted"
        icon={faCheckCircle}
      />
    </div>
  );
};

const NotFoundCaseContent: React.FC<{
  orgId: string;
  workspaceId: string;
  flowId: string;
}> = ({ orgId, workspaceId, flowId }) => {
  const { search } = useLocation();

  return (
    <div className="h-screen">
      <EmptyState
        actionButton={{
          label: "Go to the review queue",
          icon: faUserCircle,
          linkTo: getUrlToReviewQueue(orgId, workspaceId, flowId, search),
        }}
        dataLoc="not-found-case"
        description="To see all manual review items, go to the review queue"
        headline="This Manual review item was not found"
        icon={faUserCircle}
      />
    </div>
  );
};
